import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import { Logger } from 'common/logger';
import { turbineDefaults, statusDenormalize } from './helpers/turbines-model';
import { enhanceSiteTurbine } from 'common/sites';
import { getSelectedParkData } from 'common/state.helper';
import { JOB_TURBINE_HISTORY_INTERVAL } from 'common/constants';
import { checkWebsocketConnection } from 'common/saga-utils';
import { isRealTurbine } from 'common/sites.util';
import { throttle } from 'lodash';

// Initialize state with plain objects and arrays
const initialState = {
    selectedTurbine: null,
    turbineChartDataName: null,
    syncedList: [],
    list: [],
    byId: {},
    historyById: {},
    polling: null,
};

const uniqueByKey = (arr, key) => {
    return Array.from(new Map(arr.map((item) => [item[key], item])).values());
};

const turbines = {
    name: 'turbines',
    state: initialState,

    reducers: {
        updateMultiple(state, { list }) {
            const extended = list.map((turbineRaw) => ({
                ...turbineRaw,
                ...statusDenormalize(turbineRaw),
            }));
            extended.forEach((item) => {
                state.byId[item.entityKey] = {
                    ...(state.byId[item.entityKey] || {}),
                    ...item,
                    secondaryStatus: [...item.secondaryStatus, ...(state.byId[item.entityKey]?.['noteType'] || [])],
                    notes: state.byId[item.entityKey]?.['notes'] || [],
                    exceptions: state.byId[item.entityKey]?.['exceptions'] || [],
                };
            });
        },
        updateMultipleNotesAndException(state, { list }) {
            const uniqueList = uniqueByKey(list, 'entityKey');
            const tempUpdatedKeyValue = {};
            uniqueList.forEach(({ note_type, turbine_keys, description, start_time }) => {
                const noteType = note_type.value === 1 ? 'has-note' : note_type.value === 2 ? 'has-exception' : null;
                if (!noteType) return; // Skip invalid types

                const updateKey = noteType === 'has-note' ? 'notes' : 'exceptions'; // Determine which key to update
                const newEntry = { content: description.value, start_date: start_time.value };

                turbine_keys.value.forEach((turbineId) => {
                    const entity = state.byId[turbineId] || {};
                    const secondaryStatus = entity.secondaryStatus || [];
                    tempUpdatedKeyValue[turbineId] = tempUpdatedKeyValue[turbineId] || [];
                    tempUpdatedKeyValue[turbineId].push(newEntry);
                    // Update state
                    state.byId[turbineId] = {
                        ...entity,
                        secondaryStatus: secondaryStatus.includes(noteType)
                            ? secondaryStatus
                            : [...secondaryStatus, noteType],
                        noteType: [noteType],
                        [updateKey]: { value: tempUpdatedKeyValue[turbineId] },
                    };
                });
            });
        },

        updateOneHistory(state, payload) {
            Logger.info(`Loaded turbine ${payload.entityKey} history`);
            return {
                ...state,
                historyById: {
                    ...state.historyById,
                    [payload.entityKey]: payload,
                },
            };
        },

        updateAll(state, payload) {
            return { ...state, byId: payload };
        },

        updateAllHistory(state, payload) {
            return { ...state, historyById: payload };
        },

        setList(state, payload) {
            const isUpdated = JSON.stringify(state.list) !== JSON.stringify(payload);
            Logger.debug('Check if setList is updated:', isUpdated);
            return isUpdated ? { ...state, list: payload } : state;
        },

        setSyncedList(state, payload) {
            return { ...state, syncedList: payload };
        },

        turbineChartDataName(state, payload) {
            Logger.info(`Selected turbine tab: ${payload.turbineChartDataName}`);
            return { ...state, turbineChartDataName: payload.turbineChartDataName };
        },

        setSelectedTurbine(state, payload) {
            Logger.info(`Selected turbine: ${payload.id}`);
            return { ...state, selectedTurbine: payload.id };
        },
        clearPolling(state) {
            if (state.polling) {
                clearInterval(state.polling);
                Logger.info('Polling stopped'); // Log when polling is stopped
            }
            return { ...state, polling: null };
        },
        setPolling(state, polling) {
            return { ...state, polling };
        },
    },

    effects: (dispatch) => ({
        async startHistoryJob(_, state) {
            Logger.info('Starting turbine.startHistoryJob');
            await checkWebsocketConnection();

            const historyJob = async () => {
                try {
                    const entityKey = state.turbines.selectedTurbine;

                    if (!entityKey) {
                        Logger.debug('No selected turbine for history update.');
                    } else {
                        Logger.debug(`Requesting turbine history`);
                        dispatch({
                            type: 'webSocket/requestTurbineHistory',
                            payload: {
                                entityKey,
                                precision: 'minute',
                                count: 60,
                            },
                        });
                    }
                } catch (err) {
                    Logger.error('Error starting turbine startHistoryJob', err.message);
                }
            };
            historyJob();

            // Start the polling
            const polling = setInterval(historyJob, JOB_TURBINE_HISTORY_INTERVAL);
            dispatch.turbines.setPolling(polling);
        },
        clearHistoryJobs() {
            Logger.info('Stopping history job');
            dispatch.turbines.clearPolling(); // Clear polling when this action is dispatched
        },
        selectTurbineChart(payload) {
            dispatch.turbines.turbineChartDataName(payload);
            window.analytics.track('turbine graph', {
                graph_name: payload.turbineChartDataName,
            });
        },

        loadMultiple(payload, rootState) {
            Logger.log(`Turbines.loadMultiple Bulk updating ${payload.length}`);
            const { selectedPark } = rootState.appData;
            dispatch.turbines.updateMultiple({
                list: payload,
                selectedPark,
            });
            dispatch.filter.calculateStatus();
            dispatch.turbines.checkFilters();
            dispatch.turbines.sortTurbines();
        },
        loadMultipleNotesAndException(payload, rootState) {
            Logger.log(`Turbines.loadMultipleNotesAndException Bulk updating ${payload.length}`);
            const { selectedPark } = rootState.appData;
            dispatch.turbines.updateMultipleNotesAndException({
                list: payload,
                selectedPark,
            });
        },

        selectTurbine(payload, state) {
            const { selectedTurbine } = state.turbines;
            console.log('selectedTurbine===', selectedTurbine, payload.id);

            if (selectedTurbine === payload.id) {
                dispatch.turbines.deselectTurbine();
            } else {
                dispatch.offshorePlatforms.deselectPlatform();
                dispatch.turbines.setSelectedTurbine(payload);

                Logger.debug(`Requesting turbine history`);
                dispatch({
                    type: 'webSocket/requestTurbineHistory',
                    payload: {
                        entityKey: payload.id,
                        precision: 'minute',
                        count: 60,
                    },
                });

                dispatch.weather.setInactive();
                dispatch.logTabs.switchTabMode({ detailTabMode: true });

                window.analytics.track('turbine select', {
                    turbine: payload.id,
                });
            }
        },

        deselectTurbine(_, state) {
            const { selectedPlatform } = state.offshorePlatforms;
            dispatch.turbines.setSelectedTurbine({ id: null });

            if (!selectedPlatform) {
                dispatch.weather.setActive();
                dispatch.logTabs.switchTabMode({ detailTabMode: false });
            }
            window.analytics.track('turbine unselect', {});
        },

        reset() {
            Logger.info('Reset turbines');
            const parkData = getSelectedParkData();
            const turbineIds = map(parkData.turbines?.filter(isRealTurbine) || [], 'id');
            const turbinesWithDefaults = parkData.turbines?.filter(isRealTurbine).map((turbineConf) => ({
                ...turbineDefaults,
                ...enhanceSiteTurbine(turbineConf),
            }));
            const allTurbinesById = keyBy(turbinesWithDefaults, (o) => o.id);

            dispatch.turbines.setSyncedList(turbineIds);
            dispatch.turbines.updateAll(allTurbinesById);
            dispatch.turbines.setList(turbineIds);
            dispatch.turbines.updateAllHistory({});
            dispatch.turbines.clearHistoryJobs();
            dispatch.turbines.startHistoryJob();
        },

        async showOnLocation({ name }, state) {
            const { syncedList } = state.turbines;

            if (!name) {
                Logger.log('Showing all turbines:', syncedList);
                dispatch.turbines.setList(syncedList);
                dispatch.filter.calculateStatus();
            } else {
                const filteredTurbines = Object.values(state.turbines.byId)
                    .filter((turbine) => turbine.location === name)
                    .map((turbine) => turbine.id);

                Logger.log('Show turbines:', filteredTurbines);
                dispatch.turbines.setList(filteredTurbines);
                dispatch.turbines.sortTurbines(name);
                dispatch.filter.calculateStatus();
            }
        },

        checkFilters(_, state) {
            const turbinesStore = state.turbines;
            const filters = state.filter;

            const activeStatusFilters = filters.statusFilters.filter((f) => f.isActive).map((f) => f.name);
            const activeSecondaryStatusFilters = filters.secondaryStatusFilters
                .filter((f) => f.isActive)
                .map((f) => f.name);

            const noFilterActive = activeSecondaryStatusFilters.length === 0 && activeStatusFilters.length === 0;

            if (noFilterActive) {
                Logger.log('Checking filters, undimming all');
            } else {
                Logger.log('Checking filters, dimming');
            }

            const updatedById = {};
            Object.entries(turbinesStore.byId).forEach(([id, turbine]) => {
                const hasStatus = activeStatusFilters.includes(turbine.status);
                const hasSecondaryStatus = turbine.secondaryStatus.some((status) =>
                    activeSecondaryStatusFilters.includes(status)
                );

                updatedById[id] = { ...turbine, dimmed: !(noFilterActive || hasStatus || hasSecondaryStatus) };
            });

            dispatch.turbines.updateAll(updatedById);
        },

        sortTurbines: throttle((_, rootState) => {
            const { turbines, filter } = rootState;

            const turbinesList = [...turbines.list].sort();

            const { activeSortName } = filter;
            Logger.log(`Sorting turbines by ${activeSortName}`);

            const sortIn = activeSortName.split('.');

            turbinesList.sort((currentTurbineId, nextTurbineId) => {
                const currentTurbine = turbines.byId[currentTurbineId];
                const nextTurbine = turbines.byId[nextTurbineId];
                if (!currentTurbine && !nextTurbine) return 0;
                const currentTurbineValue =
                    sortIn.length === 2 ? currentTurbine[sortIn[0]][sortIn[1]] : currentTurbine[sortIn[0]];
                const nextTurbineValue =
                    sortIn.length === 2 ? nextTurbine[sortIn[0]][sortIn[1]] : nextTurbine[sortIn[0]];
                if (currentTurbineValue > nextTurbineValue) {
                    return activeSortName === 'string' ? 1 : -1;
                }
                if (currentTurbineValue < nextTurbineValue) {
                    return activeSortName === 'string' ? -1 : 1;
                }
                return 0;
            });

            dispatch.turbines.setList(turbinesList);
        }, 500),
    }),
};

export default turbines;
